/**
 * Dashboard Service - Handles API interactions for dashboard data
 */

import { get, post } from '../utils/apiUtils';

// Add debugging logs
console.log('Environment variables:', {
  REACT_APP_API_BASE_URL: process.env.REACT_APP_API_BASE_URL,
  NODE_ENV: process.env.NODE_ENV
});

const API_URL = process.env.REACT_APP_API_BASE_URL;

if (!API_URL) {
  console.error('API_URL is undefined. Make sure your .env file is properly configured and you have restarted your development server.');
}

/**
 * Get dashboard metrics
 * @param {number} startDate - Start timestamp
 * @param {number} endDate - End timestamp
 * @returns {Promise<Object>} Promise resolving to dashboard metrics
 */
export const getDashboardMetrics = async (startDate, endDate) => {
  try {
    const requestBody = {
      baseCompanyCd: "MMS",
      businessKey: "BlueMormon",
      startDate,
      endDate
    };
    const response = await post(API_URL + '/dashboard', requestBody);
    return response;
  } catch (error) {
    console.error('Error fetching dashboard metrics:', error);
    throw error;
  }
};

/**
 * Get actionable tasks for the dashboard
 * @param {string} userId - User ID
 * @returns {Promise<Array>} Promise resolving to array of actionable tasks
 */
export const getActionableTasks = async (userId) => {
  try {
    const response = await get('/tasks', { params: { userId } });
    return response.data;
  } catch (error) {
    console.error('Error fetching actionable tasks:', error);
    throw error;
  }
};

/**
 * Get system alerts for the dashboard
 * @param {Object} filters - Filter parameters
 * @returns {Promise<Array>} Promise resolving to array of system alerts
 */
export const getSystemAlerts = async (filters = {}) => {
  try {
    const response = await get('/alerts', { params: filters });
    return response.data;
  } catch (error) {
    console.error('Error fetching system alerts:', error);
    throw error;
  }
};

/**
 * Get recent activities for the dashboard
 * @param {string} userId - User ID
 * @param {number} limit - Maximum number of activities to return
 * @returns {Promise<Array>} Promise resolving to array of recent activities
 */
export const getRecentActivities = async (userId, limit = 10) => {
  try {
    const response = await get('/activities', { params: { userId, limit } });
    return response.data;
  } catch (error) {
    console.error('Error fetching recent activities:', error);
    throw error;
  }
};

/**
 * Get system performance data for the dashboard
 * @param {Object} filters - Filter parameters
 * @returns {Promise<Object>} Promise resolving to system performance data
 */
export const getSystemPerformance = async (filters = {}) => {
  try {
    const response = await get('/performance', { params: filters });
    return response.data;
  } catch (error) {
    console.error('Error fetching system performance:', error);
    throw error;
  }
};

/**
 * Get key performance indicators (KPIs)
 * @param {Object} filters - Filter parameters
 * @returns {Promise<Array>} Promise resolving to array of KPIs
 */
export const getKPIs = async (filters = {}) => {
  try {
    const response = await get('/kpis', { params: filters });
    return response.data;
  } catch (error) {
    console.error('Error fetching KPIs:', error);
    throw error;
  }
};

/**
 * Get dashboard widgets configuration
 * @param {string} userId - User ID
 * @returns {Promise<Array>} Promise resolving to array of widget configurations
 */
export const getDashboardWidgets = async (userId) => {
  try {
    const response = await get('/widgets', { params: { userId } });
    return response.data;
  } catch (error) {
    console.error('Error fetching dashboard widgets:', error);
    throw error;
  }
};

/**
 * Update dashboard widgets configuration
 * @param {string} userId - User ID
 * @param {Array} widgets - Updated widget configurations
 * @returns {Promise<Array>} Promise resolving to updated widget configurations
 */
export const updateDashboardWidgets = async (userId, widgets) => {
  try {
    const response = await post('/widgets', { userId, widgets });
    return response.data;
  } catch (error) {
    console.error('Error updating dashboard widgets:', error);
    throw error;
  }
};

/**
 * Get dashboard summary for a specific time period
 * @param {string} period - Time period (e.g., 'day', 'week', 'month')
 * @returns {Promise<Object>} Promise resolving to dashboard summary
 */
export const getDashboardSummary = async (period = 'day') => {
  try {
    const response = await get('/summary', { params: { period } });
    return response.data;
  } catch (error) {
    console.error(`Error fetching dashboard summary for period ${period}:`, error);
    throw error;
  }
};

/**
 * Get user-specific dashboard preferences
 * @param {string} userId - User ID
 * @returns {Promise<Object>} Promise resolving to dashboard preferences
 */
export const getDashboardPreferences = async (userId) => {
  try {
    const response = await get('/preferences', { params: { userId } });
    return response.data;
  } catch (error) {
    console.error('Error fetching dashboard preferences:', error);
    throw error;
  }
};

/**
 * Update user-specific dashboard preferences
 * @param {string} userId - User ID
 * @param {Object} preferences - Updated preferences
 * @returns {Promise<Object>} Promise resolving to updated preferences
 */
export const updateDashboardPreferences = async (userId, preferences) => {
  try {
    const response = await post('/preferences', { userId, preferences });
    return response.data;
  } catch (error) {
    console.error('Error updating dashboard preferences:', error);
    throw error;
  }
};

export default {
  getDashboardMetrics,
  getActionableTasks,
  getSystemAlerts,
  getRecentActivities,
  getSystemPerformance,
  getKPIs,
  getDashboardWidgets,
  updateDashboardWidgets,
  getDashboardSummary,
  getDashboardPreferences,
  updateDashboardPreferences
};
